import Request from '@/services/util/request-service';
import LocalStorageService from '@/services/storage/general/local-storage-service';
import IndexedDBService from '@/services/storage/general/indexeddb-service';
import SessionService from '@/services/storage/session-service';
import { ProfileData, InterestWeights } from 'profile-types';

const PROFILE_STORAGE_KEY = 'U_PROFILE';

const ProfileService = {
  // API requests
  async getProfile() {
    const response = await (await Request.get('user/profile'))?.json();
    return response;
  },
  async patchProfile(data: ProfileData) {
    const response = await (await Request.post('user/profile', data))?.json();
    return response;
  },
  async getUserContext() {
    const response = await (await Request.get('user/profile/context'))?.json();
    return response;
  },

  async patchInterestWeights(data: InterestWeights) {
    const response = await (await Request.post('user/profile/interests/weights', data))?.json();
    return response;
  },

  // Local storage
  getProfileCookie(): ProfileData | undefined {
    return LocalStorageService.get(PROFILE_STORAGE_KEY) as ProfileData | undefined;
  },

  setProfileCookie(data: ProfileData): void {
    LocalStorageService.set(PROFILE_STORAGE_KEY, data);

    if (IndexedDBService.available()) {
      void IndexedDBService.set(PROFILE_STORAGE_KEY, data).catch((e) =>
        console.warn('IndexedDB set failed for profile:', e),
      );
    }
    return;
  },

  resetProfileCookie(): void {
    LocalStorageService.delete(PROFILE_STORAGE_KEY);
    if (IndexedDBService.available()) {
      void IndexedDBService.delete(PROFILE_STORAGE_KEY).catch((e) =>
        console.warn('IndexedDB delete failed for profile:', e),
      );
    }
    return;
  },

  // IndexedDB
  async getProfileIndexedDB(): Promise<ProfileData | undefined> {
    try {
      if (!IndexedDBService.available()) throw new Error('IndexedDB is not available');
      const profile = await IndexedDBService.get<ProfileData>(PROFILE_STORAGE_KEY);
      if (!profile) throw new Error(`${PROFILE_STORAGE_KEY} does not exist`);
      return profile;
    } catch (e) {
      console.warn('IndexedDB failed to retrieve profile interests and goals:', e);
      return undefined;
    }
  },

  async setProfileIndexedDB(data: ProfileData): Promise<void> {
    if (!IndexedDBService.available()) return;
    try {
      await IndexedDBService.set(PROFILE_STORAGE_KEY, data);
    } catch (e) {
      console.warn('IndexedDB direct set failed for profile:', e);
    }
  },

  async resetProfileIndexedDB(): Promise<void> {
    if (!IndexedDBService.available()) return;
    try {
      await IndexedDBService.delete(PROFILE_STORAGE_KEY);
    } catch (e) {
      console.warn('IndexedDB direct delete failed for profile:', e);
    }
  },

  // Get and set profile data
  async getProfileData(): Promise<ProfileData | undefined> {
    try {
      // backend returns profile data as empty arrays if no profile exists
      const profile: ProfileData = await this.getProfile();
      if (!profile) {
        throw new Error('No profile found');
      }
      this.setProfileCookie(profile);
      return profile;
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error('getProfile failed to retrieve profile interests and goals:', e.message);
      }
      this.setProfileCookie({ interests: [], learning_goals: [] });
      return undefined;
    }
  },

  // Try to hydrate from LocalStorage first, then IndexedDB, then API
  async fetchProfileData(): Promise<ProfileData | undefined> {
    const hasCoreProfileFields = (data: ProfileData | undefined): data is ProfileData => {
      if (!data) return false;
      return Boolean(
        data.first_name ||
          data.last_name ||
          data.email ||
          data.bio ||
          data.title ||
          data.timezone ||
          data.profile_photo,
      );
    };

    const cookie = this.getProfileCookie();

    if (hasCoreProfileFields(cookie)) {
      return cookie;
    }

    const indexedDBProfile = await this.getProfileIndexedDB();
    if (hasCoreProfileFields(indexedDBProfile)) {
      this.setProfileCookie(indexedDBProfile);
      return indexedDBProfile;
    }

    const session = SessionService.getSessionCookie();
    if (session) {
      const profile = await this.getProfileData();
      return profile;
    }

    return cookie;
  },

  async setProfileData(profile: ProfileData): Promise<ProfileData> {
    try {
      const session = SessionService.getSessionCookie();
      if (!session) {
        throw new Error('No session found');
      }
      const response = await this.patchProfile(profile);

      if (response?.error) {
        throw new Error(response.error);
      }

      if (!response?.profile) {
        throw new Error('Invalid server response');
      }

      this.setProfileCookie(response.profile);
      void this.setProfileIndexedDB(response.profile);
      return response.profile;
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error('setProfile failed to set profile interests and goals:', e.message);
        throw e;
      }
      throw new Error('Unknown error saving profile');
    }
  },
};

export default ProfileService;
